import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, LoadingQuestion, TimerBar, getPlayerHeaderLeftButton } from '../shared';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import type { GameStatistics } from '../../types/game';
import { HouseRunTaskCard, HouseRunPhotoSubmit, HouseRunVotePanel } from './houseRun';
import type { HouseRunTask } from './houseRun/HouseRunTaskCard';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/player-screen.css';
import { useTranslation } from '../../i18n';

interface HouseRunQuestion {
  questionId: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  teamId: 'A' | 'B';
  task?: HouseRunTask;
  submitted?: boolean;
  teamResolved?: boolean;
  accepted?: boolean;
  awardedPoints?: number;
  opposingSubmitted?: boolean;
  opposingResolved?: boolean;
  opposingAccepted?: boolean;
  judgePhoto?: string;
  judgeSubmitterName?: string;
  acceptVotes?: number;
  rejectVotes?: number;
  hasVoted?: boolean;
  questionNumber?: number;
  totalQuestions?: number;
}

interface HouseRunPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { score: number; name: string }; B: { score: number; name: string } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: {
      questionId?: string;
      id?: string;
      timeLimit?: number;
      startTime?: number;
      questionNumber?: number;
      totalQuestions?: number;
      isHouseRun?: boolean;
    };
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const HouseRunPlayerDisplay: React.FC<HouseRunPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onLoadingTimeout,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const [hrQuestion, setHrQuestion] = useState<HouseRunQuestion | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<
    Array<{ playerId: string; playerName: string; score: number }>
  >([]);
  const [myScore, setMyScore] = useState(0);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [judgePhoto, setJudgePhoto] = useState<string | undefined>();
  const [acceptVotes, setAcceptVotes] = useState(0);
  const [rejectVotes, setRejectVotes] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const myTeamIdRef = useRef<'A' | 'B' | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) {
        setMyScore(me.score);
        if (me.teamId) myTeamIdRef.current = me.teamId;
      }
    }
  }, [roomState.players, myPlayerId]);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setHrQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setHrQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      currentQuestionIdRef.current = undefined;
      setJudgePhoto(undefined);
      setHasVoted(false);
      setAcceptVotes(0);
      setRejectVotes(0);
      setUploadError(null);
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    if (!hrQuestion && roomState.currentQuestion?.isHouseRun) {
      const q = roomState.currentQuestion;
      const questionId = q.questionId || q.id;
      if (questionId && socket && roomState.roomName) {
        socket.emit('get-house-run-state', { roomName: roomState.roomName, questionId });
      }
    }
  }, [roomState.currentQuestion, hrQuestion, socket, roomState.roomName]);

  // If we know opposing submitted but photo never arrived, re-fetch player state.
  useEffect(() => {
    if (!hrQuestion?.opposingSubmitted || judgePhoto || hrQuestion.opposingResolved) return;
    const questionId = hrQuestion.questionId;
    if (!questionId || !roomState.roomName) return;
    const t = window.setTimeout(() => {
      socket.emit('get-house-run-state', { roomName: roomState.roomName, questionId });
    }, 800);
    return () => window.clearTimeout(t);
  }, [
    hrQuestion?.opposingSubmitted,
    hrQuestion?.opposingResolved,
    hrQuestion?.questionId,
    judgePhoto,
    roomState.roomName,
    socket,
  ]);

  useEffect(() => {
    const handleQuestion = (data: HouseRunQuestion) => {
      const actualPlayerId = myPlayerId || socket.id;
      if (data.playerId && data.playerId !== actualPlayerId && data.playerId !== socket.id) return;

      const isNewRound = data.questionId !== currentQuestionIdRef.current;
      currentQuestionIdRef.current = data.questionId;
      myTeamIdRef.current = data.teamId;
      setHrQuestion(data);
      setTimeRemaining(data.timeLimit - (Date.now() - data.startTime));
      setUploading(false);
      setUploadError(null);
      if (isNewRound) {
        setPhotoKey((k) => k + 1);
        setHasVoted(!!data.hasVoted);
      } else if (data.hasVoted) {
        setHasVoted(true);
      }
      if (data.judgePhoto) {
        setJudgePhoto(data.judgePhoto);
      } else if (isNewRound || data.opposingResolved) {
        setJudgePhoto(undefined);
      }
      setAcceptVotes(data.acceptVotes ?? 0);
      setRejectVotes(data.rejectVotes ?? 0);
    };

    const handleSubmitted = (data: { questionId: string }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setHrQuestion((prev) => (prev ? { ...prev, submitted: true } : prev));
      setUploading(false);
      setUploadError(null);
      setPhotoKey((k) => k + 1);
    };

    const handleJudge = (data: {
      questionId: string;
      photoData: string;
      submitterName?: string;
      acceptVotes?: number;
      rejectVotes?: number;
      teamId?: 'A' | 'B';
    }) => {
      // Accept if we already know this round, or room says this is the active house-run question
      // (covers judge event arriving before house-run-question).
      const roomQid =
        roomState.currentQuestion?.questionId || roomState.currentQuestion?.id;
      const matches =
        data.questionId === currentQuestionIdRef.current ||
        (!currentQuestionIdRef.current && data.questionId === roomQid);
      if (!matches) return;

      if (!currentQuestionIdRef.current) {
        currentQuestionIdRef.current = data.questionId;
      }
      setJudgePhoto(data.photoData);
      setAcceptVotes(data.acceptVotes ?? 0);
      setRejectVotes(data.rejectVotes ?? 0);
      setHasVoted(false);
      setHrQuestion((prev) =>
        prev
          ? { ...prev, opposingSubmitted: true, judgeSubmitterName: data.submitterName }
          : prev
      );
    };

    const handleVoteUpdate = (data: {
      questionId: string;
      teamId?: 'A' | 'B';
      acceptVotes: number;
      rejectVotes: number;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      // Only show tally for the opposing team's photo we're judging
      if (data.teamId && myTeamIdRef.current && data.teamId === myTeamIdRef.current) return;
      setAcceptVotes(data.acceptVotes);
      setRejectVotes(data.rejectVotes);
    };

    const handleTeamResolved = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      accepted: boolean;
      points?: number;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setHrQuestion((prev) => {
        if (!prev) return prev;
        if (data.teamId === prev.teamId) {
          return {
            ...prev,
            teamResolved: true,
            accepted: data.accepted,
            awardedPoints: data.points,
          };
        }
        return { ...prev, opposingResolved: true, opposingAccepted: data.accepted };
      });
      if (myTeamIdRef.current && data.teamId !== myTeamIdRef.current) {
        setJudgePhoto(undefined);
      }
    };

    const handleError = (data: { message: string }) => {
      setUploading(false);
      setUploadError(data.message || t('gameplay.houseRun.photoTooLarge'));
    };

    socket.on('house-run-question', handleQuestion);
    socket.on('house-run-submitted', handleSubmitted);
    socket.on('house-run-judge', handleJudge);
    socket.on('house-run-vote-update', handleVoteUpdate);
    socket.on('house-run-team-resolved', handleTeamResolved);
    socket.on('house-run-error', handleError);

    return () => {
      socket.off('house-run-question', handleQuestion);
      socket.off('house-run-submitted', handleSubmitted);
      socket.off('house-run-judge', handleJudge);
      socket.off('house-run-vote-update', handleVoteUpdate);
      socket.off('house-run-team-resolved', handleTeamResolved);
      socket.off('house-run-error', handleError);
    };
  }, [socket, myPlayerId, roomState.currentQuestion, t]);

  useEffect(() => {
    if (!hrQuestion || hrQuestion.teamResolved) return;
    const interval = setInterval(() => {
      const remaining = hrQuestion.timeLimit - (Date.now() - hrQuestion.startTime);
      setTimeRemaining(Math.max(0, remaining));
    }, 100);
    return () => clearInterval(interval);
  }, [hrQuestion]);

  const handlePhotoSubmit = (photoData: string) => {
    if (!hrQuestion || hrQuestion.submitted || uploading) return;
    setUploading(true);
    setUploadError(null);
    socket.emit('house-run-submit', {
      roomName: roomState.roomName,
      questionId: hrQuestion.questionId,
      photoData,
    });
  };

  const handleVote = (vote: 'accept' | 'reject') => {
    if (!hrQuestion || hasVoted || !judgePhoto) return;
    const opposingTeam = hrQuestion.teamId === 'A' ? 'B' : 'A';
    setHasVoted(true);
    socket.emit('house-run-vote', {
      roomName: roomState.roomName,
      questionId: hrQuestion.questionId,
      teamId: opposingTeam,
      vote,
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      setGameEnded(false);
      setHrQuestion(null);
      setFinalLeaderboard([]);
      socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
    };

    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard.length > 0
        ? finalLeaderboard
        : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameType={roomState.gameType}
        finalLeaderboard={teamLeaderboard}
        myPlayerId={myPlayerId}
        myTeamId={myTeamIdRef.current ?? roomState.players.find((p) => p.id === myPlayerId)?.teamId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        statistics={gameStatistics ?? gameEndData?.gameStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!hrQuestion) {
    if (roomState.currentQuestion?.isHouseRun) {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          isHost={isHost}
          onBackToGameSelection={onBackToGameSelection}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app">
        <Header
          leftButton={getPlayerHeaderLeftButton({
              isHost,
              onBackToGameSelection,
              onGoHome,
              backTooltip: t('gameplay.backToGameSelection'),
              homeTooltip: t('common.home'),
            })}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner message={t('gameplay.houseRun.waiting')} isLoading />
        </div>
      </div>
    );
  }

  const teamScore = roomState.teams?.[hrQuestion.teamId]?.score ?? 0;
  const canHunt = !hrQuestion.submitted && !hrQuestion.teamResolved;
  const showJudge = !!judgePhoto && !hrQuestion.opposingResolved;
  // Prefer vote UI when both are available so the screen isn't cramped.
  const showHuntPrimary = canHunt && !showJudge;
  const showHuntSecondary = canHunt && showJudge;

  return (
    <div className="app">
      <Header
        leftButton={getPlayerHeaderLeftButton({
            isHost,
            onBackToGameSelection,
            onGoHome,
            backTooltip: t('gameplay.backToGameSelection'),
            homeTooltip: t('common.home'),
          })}
        roomCode={roomState.roomName}
      />
      <div className="player-display" style={{ padding: '1rem', paddingTop: '1.25rem', width: '100%' }}>
        <div
          style={{
            maxWidth: '480px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
              {t('gameplay.houseRun.round', {
                current: hrQuestion.questionNumber ?? 1,
                total: hrQuestion.totalQuestions ?? 1,
              })}
            </span>
          </div>

          <TimerBar timeRemaining={timeRemaining} timeLimit={hrQuestion.timeLimit} />

          <div style={{ textAlign: 'center', fontSize: '0.9rem', opacity: 0.85 }}>
            {t('gameplay.houseRun.teamScore', { team: hrQuestion.teamId, score: teamScore })}
          </div>

          {hrQuestion.task && !showJudge && (
            <HouseRunTaskCard task={hrQuestion.task} teamId={hrQuestion.teamId} />
          )}

          {showJudge && (
            <HouseRunVotePanel
              photoData={judgePhoto}
              submitterName={hrQuestion.judgeSubmitterName}
              acceptVotes={acceptVotes}
              rejectVotes={rejectVotes}
              hasVoted={hasVoted}
              disabled={timeRemaining <= 0}
              onVote={handleVote}
            />
          )}

          {showHuntPrimary && (
            <HouseRunPhotoSubmit
              key={photoKey}
              disabled={timeRemaining <= 0}
              uploading={uploading}
              onSubmit={handlePhotoSubmit}
            />
          )}

          {showHuntSecondary && (
            <details style={{ marginTop: '0.25rem' }}>
              <summary style={{ cursor: 'pointer', opacity: 0.85, fontSize: '0.9rem' }}>
                {t('gameplay.houseRun.yourTaskStillOpen')}
              </summary>
              <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {hrQuestion.task && <HouseRunTaskCard task={hrQuestion.task} teamId={hrQuestion.teamId} />}
                <HouseRunPhotoSubmit
                  key={`${photoKey}-secondary`}
                  disabled={timeRemaining <= 0}
                  uploading={uploading}
                  onSubmit={handlePhotoSubmit}
                />
              </div>
            </details>
          )}

          {uploadError && (
            <p style={{ color: '#F87171', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>
              {uploadError}
            </p>
          )}

          {hrQuestion.submitted && !hrQuestion.teamResolved && (
            <p style={{ textAlign: 'center', margin: 0, opacity: 0.8, fontSize: '0.95rem' }}>
              {t('gameplay.houseRun.waitingForVote')}
            </p>
          )}

          {hrQuestion.teamResolved && (
            <p
              style={{
                textAlign: 'center',
                margin: 0,
                fontSize: '1rem',
                fontWeight: 700,
                color: hrQuestion.accepted ? '#22C55E' : '#F87171',
              }}
            >
              {hrQuestion.accepted
                ? t('gameplay.houseRun.accepted', { points: hrQuestion.awardedPoints ?? 0 })
                : t('gameplay.houseRun.rejected')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HouseRunPlayerDisplay;
